import { useState, useEffect, useRef } from 'react'
import { ArrowRight, Shield, Zap, Award, ChevronLeft, ChevronRight, Factory, Users, Globe, Target, X } from 'lucide-react'
import { CAROUSEL_IMAGES, QUALIFICATION_IMAGES } from '../config/assets'

const {
  QUALIFICATION: QualificationImage,
  PATENT_1: PatentImage,
  PATENT_2: PatentImage2,
  PATENT_3: PatentImage3,
  PATENT_4: PatentImage4,
  PATENT_5: PatentImage5,
  PATENT_6: PatentImage6,
  PATENT_7: PatentImage7,
  PATENT_8: PatentImage8,
  PATENT_9: PatentImage9,
  PATENT_10: PatentImage10,
  PATENT_11: PatentImage11,
  PATENT_12: PatentImage12,
  PATENT_13: PatentImage13
} = QUALIFICATION_IMAGES

const {
  AIR_HEATER_1: CarouselImage1,
  AIR_HEATER_2: CarouselImage2,
  WATER_HEATER_1: CarouselImage3
} = CAROUSEL_IMAGES

const features = [
  { icon: Shield, text: '品质保障' },
  { icon: Zap, text: '高效节能' },
  { icon: Award, text: '技术领先' },
]

const carouselItems = [
  {
    id: 1,
    title: '云净炉业',
    subtitle: '引领清洁能源新时代',
    description: '专注于风暖炉、水暖炉、燃烧机、燃木壁炉等工业炉设备的研发与制造，为您提供高效、节能、环保的加热解决方案',
    buttonText: '探索产品',
    image: CarouselImage1
  },
  {
    id: 2,
    title: '专业制造',
    subtitle: '匠心品质，精益求精',
    description: '拥有现代化生产基地和先进的生产设备，严格的质量管理体系，确保每一件产品都符合高品质标准',
    buttonText: '了解更多',
    image: CarouselImage2
  },
  {
    id: 3,
    title: '服务保障',
    subtitle: '专业团队，贴心服务',
    description: '提供从产品选型、方案设计到安装调试、运维培训的一站式服务，让您无后顾之忧',
    buttonText: '联系我们',
    image: CarouselImage3
  }
]

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [currentImage, setCurrentImage] = useState('')
  const heroRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleParallax = () => {
      if (heroRef.current) {
        const scrolled = window.scrollY
        const rate = scrolled * 0.3
        heroRef.current.style.transform = `translateY(${rate}px)`
      }
    }
    window.addEventListener('scroll', handleParallax)
    return () => window.removeEventListener('scroll', handleParallax)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  const openViewer = (imageUrl: string) => {
    setCurrentImage(imageUrl)
    setViewerOpen(true)
  }



  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselItems.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length)
  }

  return (
    <>
      {/* Hero Carousel */}
      <section id="home" className="relative min-h-[80vh] sm:min-h-[90vh] flex items-center justify-center overflow-hidden w-full">
        {/* Background Images */}
        <div className="absolute inset-0 h-full w-full">
          {carouselItems.map((item, index) => (
            <div 
              key={item.id}
              className={`absolute inset-0 h-full w-full transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
            >
              <img 
                src={item.image} 
                alt={item.title} 
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center' }}
              />
            </div>
          ))}
          {/* Overlay */}
          <div className="absolute inset-0 h-full w-full bg-black/40" />
          {/* Grid pattern overlay */}
          <div 
            className="absolute inset-0 h-full w-full opacity-20"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '5vw 5vw'
            }}
          />
        </div>

        {/* Carousel Content */}
        <div ref={heroRef} className="relative z-10 max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-10 sm:py-20 w-full overflow-hidden">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6 sm:mb-8 animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-accent-orange animate-pulse" />
              <span className="text-sm text-white/90 font-medium">专业供暖设备制造商</span>
            </div>

            {/* Carousel Slides */}
            <div className="relative">
              {carouselItems.map((item, index) => (
                <div 
                  key={item.id}
                  className={`transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
                >
                  {/* Main heading */}
                  <h1 className="text-[40px] sm:text-[60px] md:text-[75px] lg:text-[100px] font-bold text-white mb-4 sm:mb-6 leading-tight animate-slide-up">
                    {item.title}
                    <span className="block mt-2 bg-clip-text text-transparent bg-gradient-to-r from-primary-300 to-accent-orange">
                      {item.subtitle}
                    </span>
                  </h1>
                </div>
              ))}

              {/* Carousel Controls */}
              <div className="absolute top-1/2 left-2 sm:left-4 md:left-8 transform -translate-y-1/2">
                <button 
                  onClick={prevSlide}
                  className="w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20 transition-colors flex items-center justify-center"
                >
                  <ChevronLeft className="w-4 sm:w-5 md:w-6 h-4 sm:h-5 md:h-6" />
                </button>
              </div>
              <div className="absolute top-1/2 right-2 sm:right-4 md:right-8 transform -translate-y-1/2">
                <button 
                  onClick={nextSlide}
                  className="w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20 transition-colors flex items-center justify-center"
                >
                  <ChevronRight className="w-4 sm:w-5 md:w-6 h-4 sm:h-5 md:h-6" />
                </button>
              </div>

              {/* Carousel Indicators */}
              <div className="flex items-center justify-center gap-2 mt-6 sm:mt-8">
                {carouselItems.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-white w-6 sm:w-8' : 'bg-white/50'}`}
                  />
                ))}
              </div>
            </div>

            {/* Features */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 animate-slide-up mt-8 sm:mt-12" style={{ animationDelay: '0.3s' }}>
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 sm:gap-3 text-white/80">
                  <div className="p-2 rounded-lg bg-white/10 backdrop-blur-sm">
                    <feature.icon className="w-4 sm:w-5 h-4 sm:h-5 text-primary-300" />
                  </div>
                  <span className="font-medium text-sm sm:text-base">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-5 sm:h-16 bg-gradient-to-t from-secondary-50 to-transparent" />
      </section>

      {/* Company Introduction */}
      <section className="py-8 sm:py-16 md:py-20 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
          {/* 公司简介标签 - 页面顶部位置 */}
          <div className="text-center mb-8 sm:mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold">
              公司简介
            </span>
          </div>
          
          <div className="space-y-8 sm:space-y-12">
            {/* 公司介绍部分 */}
            <div>
              <div className="mb-4 sm:mb-6 text-center">
                <h2 className="text-5vw sm:text-6vw font-bold text-secondary-900">
                  关于云净炉业
                </h2>
              </div>
              <p className="text-sm sm:text-lg text-secondary-600 mb-4 sm:mb-6 leading-relaxed" style={{ textIndent: '2em' }}>
                云净炉业有限公司成立于2009年，是一家集研发、生产、销售、服务于一体的专业供暖设备制造商。
                公司专注于清洁能源利用技术的研发与应用，产品涵盖风暖炉、水暖炉、燃烧机、燃木壁炉等多个系列，
                广泛应用于工业加热、农业烘干、商业供暖等领域。
              </p>
              <p className="text-sm sm:text-lg text-secondary-600 mb-6 sm:mb-8 leading-relaxed" style={{ textIndent: '2em' }}>
                我们始终坚持"科技创新、质量为本、客户至上"的经营理念，不断引进国内外先进技术，
                建立了完善的质量管理体系和售后服务网络，为客户提供从产品选型、方案设计到安装调试、运维培训的一站式服务。
              </p>
              <a href="/about" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors">
                了解更多
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>

            {/* 证书展示部分 */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                {/* 认证资质展示区域 - 三部分布局 */}
                <div className="space-y-3 sm:space-y-4">
                  {/* 第一部分：认证资质标语 */}
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={QualificationImage}
                      alt="认证资质"
                      className="w-full h-auto object-contain"
                    />
                  </div>

                  {/* 第二部分：证书展示 */}
                  <div className="rounded-2xl overflow-hidden bg-white p-4">
                    <div className="overflow-x-auto whitespace-nowrap py-4 scrollbar-hide">
                      <div className="inline-flex gap-4 animate-scroll">
                        {[
                          ...[PatentImage6, PatentImage7, PatentImage8, PatentImage9, PatentImage10, PatentImage11, PatentImage12, PatentImage13],
                          ...[PatentImage6, PatentImage7, PatentImage8, PatentImage9, PatentImage10, PatentImage11, PatentImage12, PatentImage13]
                        ].map((image, index) => (
                          <div 
                            key={index}
                            className="relative overflow-hidden rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all transform hover:-translate-y-1 flex-shrink-0 w-28 sm:w-36"
                            onClick={() => openViewer(image)}
                          >
                            <img
                              src={image}
                              alt={`资质专利${(index % 8) + 6}`}
                              className="w-full h-40 sm:h-56 object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                              <span className="text-white opacity-0 hover:opacity-100 transition-opacity text-sm font-medium">查看</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <style>{`
                        @keyframes scroll {
                          0% { transform: translateX(0); }
                          100% { transform: translateX(-50%); }
                        }
                        .animate-scroll {
                          display: flex;
                          animation: scroll 40s linear infinite;
                        }
                        .animate-scroll:hover {
                          animation-play-state: paused;
                        }
                        .scrollbar-hide::-webkit-scrollbar {
                          display: none;
                        }
                        .scrollbar-hide {
                          -ms-overflow-style: none;
                          scrollbar-width: none;
                        }
                      `}</style>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 奖牌展示部分 */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-white p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* 第一行：左侧大证书 */}
                  <div 
                    className="relative overflow-hidden rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all transform hover:-translate-y-1 md:col-span-1 md:row-span-2 h-full"
                    onClick={() => openViewer(PatentImage)}
                  >
                    <img
                      src={PatentImage}
                      alt="资质专利1"
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                      <span className="text-white opacity-0 hover:opacity-100 transition-opacity text-sm font-medium">查看</span>
                    </div>
                  </div>
                  
                  {/* 第一行：右侧两个证书 */}
                  <div className="md:col-span-2 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div 
                        className="relative overflow-hidden rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all transform hover:-translate-y-1 h-full"
                        onClick={() => openViewer(PatentImage2)}
                      >
                        <img
                          src={PatentImage2}
                          alt="资质专利2"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <span className="text-white opacity-0 hover:opacity-100 transition-opacity text-sm font-medium">查看</span>
                        </div>
                      </div>
                      <div 
                        className="relative overflow-hidden rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all transform hover:-translate-y-1 h-full"
                        onClick={() => openViewer(PatentImage3)}
                      >
                        <img
                          src={PatentImage3}
                          alt="资质专利3"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <span className="text-white opacity-0 hover:opacity-100 transition-opacity text-sm font-medium">查看</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* 第二行：两个证书 */}
                    <div className="grid grid-cols-2 gap-3">
                      <div 
                        className="relative overflow-hidden rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all transform hover:-translate-y-1 h-full flex items-center justify-center p-0"
                        onClick={() => openViewer(PatentImage4)}
                      >
                        <img
                          src={PatentImage4}
                          alt="资质专利4"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <span className="text-white opacity-0 hover:opacity-100 transition-opacity text-sm font-medium">查看</span>
                        </div>
                      </div>
                      <div 
                        className="relative overflow-hidden rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all transform hover:-translate-y-1 h-full flex items-center justify-center p-0"
                        onClick={() => openViewer(PatentImage5)}
                      >
                        <img
                          src={PatentImage5}
                          alt="资质专利5"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <span className="text-white opacity-0 hover:opacity-100 transition-opacity text-sm font-medium">查看</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mt-8 sm:mt-12 lg:mt-16">
            {[
              { icon: Factory, value: '15+', label: '年行业经验' },
              { icon: Users, value: '100000+', label: '服务客户' },
              { icon: Globe, value: '30+', label: '覆盖省份' },
              { icon: Target, value: '99%', label: '客户满意度' }
            ].map((stat, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-secondary-100"
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-3 rounded-xl bg-primary-50 group-hover:bg-primary-100 transition-colors">
                    <stat.icon className="w-5 sm:w-6 h-5 sm:h-6 text-primary-600" />
                  </div>
                  <div>
                    <div className="text-4vw sm:text-2xl md:text-3xl font-bold text-secondary-900">{stat.value}</div>
                    <div className="text-xs sm:text-sm text-secondary-500">{stat.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Viewer */}
      {viewerOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setViewerOpen(false)
            }
          }}
        >
          <button
            onClick={() => setViewerOpen(false)}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white bg-opacity-20 text-white flex items-center justify-center hover:bg-opacity-30 transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-5xl max-h-[90vh]">
            <img
              src={currentImage}
              alt="查看图片"
              className="w-full h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  )
}
